import {combineLatest, Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {GenericDatagridService} from '../../core/services/generics/generic-datagrid.service';
import {UtilsService} from '../../core/utils/utils.service';
import {UniteDeProductionDTO} from '../../core/dtos/unite-de-production-dto';
import {SecteurDTO} from '../../core/dtos/secteur-dto';
import {FournisseurDTO} from '../../core/dtos/fournisseur-d-t-o';
import {SecteurFournisseurDTO} from '../../core/dtos/secteurfournisseur-dto';
import {SecteursFournisseursService} from '../../core/services/entities/secteurs-fournisseurs.service';
import {UniteDeProduction__LivraisonDTO} from '../../core/dtos/unite-de-production__livraison-dto';
import {ZoneDeProductionDTO} from '../../core/dtos/zone-de-production-dto';
import {EquipeDTO} from '../../core/dtos/equipe-dto';
import {UniteDeProduction_Planning_EquipeDTO} from '../../core/dtos/unite_de_production__planning__equipe_d_t_o';
import {UnitesDeProductionService} from '../../core/services/entities/unites-de-production.service';
import {SiteDTO} from '../../core/dtos/site-dto';
import {UniteDeProduction__EquipeDTO} from '../../core/dtos/unite-de-production__equipe';
import {ZoneDeStockageDTO} from '../../core/dtos/zone-de-stockage-dto';


@Injectable()
export class UniteProductionResolverService implements Resolve<UniteDeProductionSupplier> {

  constructor(private udpSvc: UnitesDeProductionService,
              private secteursFournisseursSvc: SecteursFournisseursService,
              private gds: GenericDatagridService,
              private utils: UtilsService) {
  }

  /**
   * Charger les données necessaires àl'affichage de l'unite de production
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Promise<UniteDeProductionSupplier>}
   */
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UniteDeProductionSupplier> | Promise<UniteDeProductionSupplier> | UniteDeProductionSupplier {

    const udps = new UniteDeProductionSupplier();
    const idUniteDeProduction: number = +route.paramMap.get('idUniteDeProduction');
    let obsUniteDeProduction$ = this.gds.getOne('uniteDeProduction', idUniteDeProduction);
    let obsSecteurList$ = this.gds.getAll('secteur', ['code,asc'], true);
    let obsEquipeList$ = this.gds.getAll('equipe', ['code,asc'], true);
    let obsZoneDeProdList$ = this.gds.getAll('zoneDeProduction', ['code,asc'], true);
    let obsEquipesOfUDP$ = this.udpSvc.findEquipes(idUniteDeProduction);


    let obsLivraisonsOfUDP$ = this.secteursFournisseursSvc.findLivraisonsOfUniteDeProduction(idUniteDeProduction);
    let obsFabricationsOfUDP$ = this.udpSvc.findFabrication(idUniteDeProduction);
    let obsZonesStockageOfUDP$ = this.udpSvc.getZoneDeStockageByIdUdp(idUniteDeProduction);
    let obsZonesStockage$ = this.gds.getAll('ZoneDeStockage', ['libelle,asc'], false);

    // MODIFICATION, on recupere l'udp du back, on recupere les secteurs
    if (this.utils.isNumberGt0(idUniteDeProduction)) {

      const initAll$ = combineLatest([obsSecteurList$, obsUniteDeProduction$, obsLivraisonsOfUDP$, obsEquipeList$,
        obsZoneDeProdList$, obsFabricationsOfUDP$, obsEquipesOfUDP$, obsZonesStockageOfUDP$, obsZonesStockage$]);

      return initAll$.toPromise().then(items => {
        if (!this.utils.isResponseSupplierError(items[0]) && !this.utils.isResponseSupplierError(items[1])) {
          udps.secteurList = items[0].resultList;
          udps.uniteDeProduction = items[1].one;
          udps.livraisonsOfUDP = items[2].resultList;

          if (!this.utils.isCollectionNullOrEmpty(udps.livraisonsOfUDP)) {
            udps.livraisonsOfUDP.forEach(item => item.delaiLivraisonHeureLimite = this.utils.convertNumberDateToDate(item.delaiLivraisonHeureLimite))
          }
          udps.equipeList = items[3].resultList;
          udps.zoneDeProdList = items[4].resultList;
          udps.fabricationsOfUDP = items[5].resultList;
          udps.equipesOfUDP = items[6].resultList;
          udps.zonesStockageOfUDP = items[7].resultList;
          udps.zonesStockageList = items[8].resultList;
          //si on a des secteurs associés à l'unité de prod, on recupere les fournisseurs associés.
          if (!this.utils.isCollectionNullOrEmpty(udps.uniteDeProduction.uniteDeProduction__secteurList)) {
            let udpSecteurs: SecteurDTO[] = [];
            udpSecteurs = udps.uniteDeProduction.uniteDeProduction__secteurList.map(item => {
              let secteur = new SecteurDTO();
              secteur.id = item.idSecteur;
              return secteur;
            });

            return this.secteursFournisseursSvc.findSecteursFournisseursBySecteurs(udpSecteurs)
              .toPromise()
              .then(response => {
                if (!this.utils.isResponseSupplierError(response)) {
                  udps.secteurFournisseurList = response.resultList;
                }
                return udps;
              });
          }
          return udps;
        }
      });
    }
    // CREATION, on créé l'udp depuis le front (seulement fiche identite)
    else {
      udps.uniteDeProduction = this.initUniteDeProduction();
      return udps;
    }
  }

  initUniteDeProduction(): UniteDeProductionDTO {
    //on créé forcement l'unité de production et ses adresses sur le site principal de l'utilisateur
    let site = new SiteDTO();
    //on initialise l'up avec ses adresses
    const uniteDeProduction = new UniteDeProductionDTO(0, site);
    uniteDeProduction.libelle = 'UP';
    uniteDeProduction.code = 'UP';
    uniteDeProduction.site = site;

    return uniteDeProduction;
  }
}

export class UniteDeProductionSupplier {
  uniteDeProduction: UniteDeProductionDTO;
  secteurList: SecteurDTO[] = [];
  fournisseurList: FournisseurDTO[] = [];
  secteurFournisseurList: SecteurFournisseurDTO[] = [];
  livraisonsOfUDP: UniteDeProduction__LivraisonDTO[] = [];
  fabricationsOfUDP: UniteDeProduction_Planning_EquipeDTO[] = [];
  zoneDeProdList: ZoneDeProductionDTO[] = [];
  equipeList: EquipeDTO[] = [];
  equipesOfUDP: UniteDeProduction__EquipeDTO[] = [];
  zonesStockageOfUDP: ZoneDeStockageDTO[] = [];
  zonesStockageList: ZoneDeStockageDTO[] = [];
}

